import { Component, lazy, Suspense } from 'react';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { Toaster } from '@/pet/src/components/ui/sonner';
import { ThemeProvider } from '@/pet/src/context/ThemeContext';
import { PetsProvider } from '@/pet/src/context/PetsContext';
import { ProposedRecordsProvider } from '@/pet/src/context/ProposedRecordsContext';
import { NotificationsProvider } from '@/pet/src/context/NotificationsContext';
import { OwnerOnboardingProvider } from '@/pet/src/context/OwnerOnboardingContext';
import { AppointmentsProvider } from '@/pet/src/context/AppointmentsContext';
import { SubscriptionProvider } from '@/pet/src/context/SubscriptionContext';
import { RemindersProvider } from '@/pet/src/context/RemindersContext';
import { OnboardingProvider } from '@/pet/src/context/OnboardingContext';
import { AuthProvider } from '@/context/AuthContext';
import { Web3Provider } from '@/context/Web3Provider';
import { SocialConnectionsProvider } from '@/pet/src/context/SocialConnectionsContext';
import { StoriesProvider } from '@/pet/src/context/StoriesContext';

class DevtoolsErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError = () => ({ hasError: true });
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

const TanStackRouterDevtools =
  import.meta.env.DEV &&
  lazy(() =>
    import('@tanstack/react-router-devtools').then((m) => ({
      default: m.TanStackRouterDevtools,
    }))
  );

function RootComponent() {
  return (
    <ThemeProvider>
      <Web3Provider>
        <AuthProvider>
          <SubscriptionProvider>
            <PetsProvider>
              <ProposedRecordsProvider>
                <OwnerOnboardingProvider>
                  <OnboardingProvider>
                    <AppointmentsProvider>
                      <RemindersProvider>
                        <SocialConnectionsProvider>
                          <StoriesProvider>
                            <NotificationsProvider>
                              <Outlet />
                              <Toaster />
                              {TanStackRouterDevtools && (
                                <DevtoolsErrorBoundary>
                                  <Suspense fallback={null}>
                                    <TanStackRouterDevtools />
                                  </Suspense>
                                </DevtoolsErrorBoundary>
                              )}
                            </NotificationsProvider>
                          </StoriesProvider>
                        </SocialConnectionsProvider>
                      </RemindersProvider>
                    </AppointmentsProvider>
                  </OnboardingProvider>
                </OwnerOnboardingProvider>
              </ProposedRecordsProvider>
            </PetsProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </Web3Provider>
    </ThemeProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
